import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { toast } from 'sonner';
import { newIdempotencyKey, RECHECK_LABEL, startVerification, type BatchLead, type VerificationStart } from './api';
import { leadImportKeys } from './keys';
import { fmtInt } from './ui';

type Kind = 'phone' | 'email';
type VerifyAsk = { kinds: Kind[]; force: boolean; leads: BatchLead[]; done: () => void };

const KIND_SETS: Kind[][] = [['phone'], ['email'], ['phone', 'email']];

/**
 * Verify buttons and their confirm dialog for checked leads. A run belongs to
 * one import, so leads from several imports start one run per import.
 */
export function useVerifyLeads() {
  const queryClient = useQueryClient();
  // One idempotency key per (import, leads, checks) request until the whole
  // request succeeds: a retry after a lost response or a partial failure
  // replays the runs already started instead of paying for them again.
  const keys = useRef(new Map<string, string>());
  const [ask, setAsk] = useState<VerifyAsk | null>(null);
  const verify = useMutation({
    mutationFn: async ({ kinds, force, leads }: VerifyAsk) => {
      const byBatch = new Map<number, number[]>();
      for (const l of leads) byBatch.set(l.batch, [...(byBatch.get(l.batch) ?? []), l.id]);
      const results: VerificationStart[] = [];
      for (const [batch, ids] of byBatch) {
        const request = `${batch}|${kinds.join(',')}|${force}|${ids.join(',')}`;
        if (!keys.current.has(request)) keys.current.set(request, newIdempotencyKey());
        results.push(await startVerification(batch, ids, kinds, force, keys.current.get(request) as string));
        queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch) });
      }
      return results;
    },
    onSuccess: (results, a) => {
      keys.current.clear();
      setAsk(null);
      const sum = (pick: (r: VerificationStart) => number | undefined) => results.reduce((n, r) => n + (r.replayed ? 0 : pick(r) ?? 0), 0);
      const charged = sum((r) => (r.phone_requests ?? 0) + (r.email_requests ?? 0));
      const reused = sum((r) => (r.phone_reused ?? 0) + (r.email_reused ?? 0));
      const joined = sum((r) => (r.phone_joined ?? 0) + (r.email_joined ?? 0));
      const known = sum((r) => r.skipped_known_leads ?? 0);
      if (results.every((r) => r.replayed)) {
        toast.success('This verification was already started. No new checks were charged.');
      } else {
        const parts = [
          charged ? `${fmtInt(charged)} new ${charged === 1 ? 'check' : 'checks'}` : null,
          reused ? `${fmtInt(reused)} already verified, reused at no cost` : null,
          joined ? `${fmtInt(joined)} already being checked` : null,
          known ? `${fmtInt(known)} ${known === 1 ? 'lead' : 'leads'} already in the CRM skipped` : null,
        ].filter(Boolean);
        toast.success(parts.length ? `${parts.join(', ')}.${charged ? ' Results appear as they come in.' : ''}` : 'Nothing to check.');
      }
      queryClient.invalidateQueries({ queryKey: leadImportKeys.leadsAll });
      a.done();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const what = !ask ? '' : ask.kinds.length === 2 ? 'phones and emails' : ask.kinds[0] === 'phone' ? 'phones' : 'emails';
  const imports = ask ? new Set(ask.leads.map((l) => l.batch)).size : 0;

  const buttons = (leads: BatchLead[], done: () => void) => KIND_SETS.map((kinds) => (
    <button key={kinds.join()} type="button" className="li-btn li-btn--secondary" disabled={verify.isPending}
      onClick={() => setAsk({ kinds, force: false, leads, done })}>
      {kinds.length === 2 ? 'Verify both' : kinds[0] === 'phone' ? 'Verify phones' : 'Verify emails'}
    </button>
  ));

  const dialog = (
    <Dialog isOpen={!!ask} onClose={() => { if (!verify.isPending) setAsk(null); }} title={`Verify ${what}`} style={{ width: 520 }}>
      <DialogBody>
        <div className="li-stack">
          <p>
            Checks the {what} of {fmtInt(ask?.leads.length ?? 0)} checked leads
            {imports > 1 ? ` from ${fmtInt(imports)} imports` : ''} with the paid providers. Nobody is contacted.
          </p>
          <p className="li-muted">
            Leads that were already in the CRM when they were imported are skipped, and a phone or email verified before uses its
            stored result. Neither is charged.
          </p>
          <Checkbox checked={ask?.force ?? false} disabled={verify.isPending}
            onChange={(e) => setAsk((v) => (v ? { ...v, force: (e.target as HTMLInputElement).checked } : v))}
            label={RECHECK_LABEL} />
        </div>
      </DialogBody>
      <DialogFooter actions={<>
        <Button disabled={verify.isPending} onClick={() => setAsk(null)}>Cancel</Button>
        <Button intent="primary" loading={verify.isPending} onClick={() => { if (ask) verify.mutate(ask); }}>
          {ask?.force ? 'Verify again' : 'Verify'}
        </Button>
      </>} />
    </Dialog>
  );

  return { buttons, dialog };
}
